import React from 'react';
import { Brain, TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface AIRecommendationBadgeProps {
  recommendation: 'strong_buy' | 'buy' | 'hold' | 'sell' | 'strong_sell';
  confidence?: number;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
}

const AIRecommendationBadge: React.FC<AIRecommendationBadgeProps> = ({
  recommendation,
  confidence,
  size = 'md',
  showIcon = true,
}) => {
  const getRecommendationConfig = () => {
    switch (recommendation) {
      case 'strong_buy':
        return {
          label: 'Strong Buy',
          icon: TrendingUp,
          bgColor: 'bg-emerald-500/20',
          textColor: 'text-emerald-400',
          borderColor: 'border-emerald-500/30',
        };
      case 'buy':
        return {
          label: 'Buy',
          icon: TrendingUp,
          bgColor: 'bg-green-500/20',
          textColor: 'text-green-400',
          borderColor: 'border-green-500/30',
        };
      case 'hold':
        return {
          label: 'Hold',
          icon: Minus,
          bgColor: 'bg-yellow-500/20',
          textColor: 'text-yellow-400',
          borderColor: 'border-yellow-500/30',
        };
      case 'sell':
        return {
          label: 'Sell',
          icon: TrendingDown,
          bgColor: 'bg-orange-500/20',
          textColor: 'text-orange-400',
          borderColor: 'border-orange-500/30',
        };
      case 'strong_sell':
        return {
          label: 'Strong Sell',
          icon: TrendingDown,
          bgColor: 'bg-red-500/20',
          textColor: 'text-red-400',
          borderColor: 'border-red-500/30',
        };
      default:
        return {
          label: 'Unknown',
          icon: Minus,
          bgColor: 'bg-slate-500/20',
          textColor: 'text-slate-400',
          borderColor: 'border-slate-500/30',
        };
    }
  };

  const config = getRecommendationConfig();
  const Icon = config.icon;

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
    lg: 'px-3 py-1.5 text-base',
  };

  const iconSizes = {
    sm: 10,
    md: 12,
    lg: 14,
  };

  return (
    <div
      className={`inline-flex items-center gap-1.5 rounded-full border ${config.bgColor} ${config.textColor} ${config.borderColor} ${sizeClasses[size]} font-medium`}
      title={confidence ? `AI confidence: ${(confidence * 100).toFixed(0)}%` : undefined}
    >
      {showIcon && <Brain size={iconSizes[size]} />}
      <span>{config.label}</span>
      {confidence !== undefined && size !== 'sm' && (
        <span className="opacity-70 text-xs">({(confidence * 100).toFixed(0)}%)</span>
      )}
    </div>
  );
};

export default AIRecommendationBadge;
